<template>
  <div>
    <div
      ref="cellTypeMenu"
      class="vue-simple-context-menu vue-simple-context-menu--two-col"
    >
      <!-- Left column: normal categories -->
      <div class="vue-simple-context-menu__col vue-simple-context-menu__col--left">
        <ul class="vue-simple-context-menu__list">
          <li
            v-for="(option, index) in options"
            :key="index"
            :class="[
              'vue-simple-context-menu__item',
              option.class,
              (option.type === 'divider' ? 'vue-simple-context-menu__divider' : ''),
              (activeOption && (activeOption.name === option.name) ? 'vue-simple-context-menu__active' : '')
            ]"
            @mouseenter="setActive(option)"
            @mouseleave="clearActive"
            @click.stop="option.clickable === false ? null : optionClicked(option)"
          >
            <span
              v-if="option.type !== 'divider'"
              class="vue-simple-context-menu__label"
            >{{ option.label || option.name }}</span>
            <span
              v-else
              class="vue-simple-context-menu__label"
            >{{ option.name }}</span>
          </li>
        </ul>
      </div>
      <div class="vue-simple-context-menu__separator" />
      <!-- Right column: abnormal groups (always visible). Reuse divider style for headers. -->
      <div class="vue-simple-context-menu__col">
        <template v-for="(option, index) in options">
          <ul
            v-if="option && option.children && option.children.length"
            :key="`group-${index}`"
            :class="['vue-simple-context-menu__list', 'vue-simple-context-menu__group', (activeOption && activeOption.name === option.name) ? 'is-active' : '']"
          >
            <li class="vue-simple-context-menu__item vue-simple-context-menu__divider">
              <span class="vue-simple-context-menu__label">{{ option.label || option.name }}</span>
            </li>
            <li
              v-for="(child, cidx) in option.children"
              :key="cidx"
              :class="[
                'vue-simple-context-menu__item',
                child.class,
                (child.type === 'divider' ? 'vue-simple-context-menu__divider' : '')
              ]"
              @click.stop="child.type === 'divider' || child.clickable === false ? null : optionClicked(child)"
            >
              <span class="vue-simple-context-menu__label">{{ child.label || child.name }}</span>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';
import type { CellTypeMenuOption } from '@/utils/constants/cellTypeMenuConfig';

type MenuOrigin = {
  fromType: string;
  fromTileId: number;
};

const props = defineProps<{ options: CellTypeMenuOption[] }>();
const options = computed(() => props.options);
const emit = defineEmits<{
  (e: 'option-clicked', payload: { item: MenuOrigin; option: CellTypeMenuOption }): void;
}>();

const item = ref<MenuOrigin | null>(null);
const menuWidth = ref<number | null>(null);
const menuHeight = ref<number | null>(null);
const activeOption = ref<CellTypeMenuOption | null>(null);
const cellTypeMenu = ref<HTMLDivElement | null>(null);

function showMenu(event: MouseEvent, it: MenuOrigin) {
  item.value = it;
  const menu = cellTypeMenu.value;
  if (!menu) { return; }
  // Measure two-column menu
  menu.style.visibility = 'hidden';
  menu.style.display = 'flex';
  menuWidth.value = menu.offsetWidth;
  menuHeight.value = menu.offsetHeight;
  menu.removeAttribute('style');
  const width = menuWidth.value ?? menu.offsetWidth;
  const height = menuHeight.value ?? menu.offsetHeight;
  if ((width + event.pageX) >= window.innerWidth) {
    menu.style.left = `${event.pageX - width - 2}px`;
  } else {
    menu.style.left = `${event.pageX + 2}px`;
  }
  if ((height + event.clientY) >= window.innerHeight) {
    const tempY = event.pageY + (window.innerHeight - event.clientY) - height - 2;
    menu.style.top = (tempY > 0) ? `${tempY}px` : '2px';
  } else {
    menu.style.top = `${event.pageY - 2}px`;
  }
  menu.classList.add('vue-simple-context-menu--active');
}

function hideContextMenu() {
  const menu = cellTypeMenu.value;
  if (menu) {
    menu.classList.remove('vue-simple-context-menu--active');
  }
}

// expose methods so parent components can call `this.$refs.someRef.showMenu(...)`
defineExpose({ showMenu, hideContextMenu });

function setActive(option: CellTypeMenuOption) {
  if (option && option.children && option.children.length) {
    activeOption.value = option;
  } else {
    activeOption.value = null;
  }
}

function clearActive() {
  activeOption.value = null;
}

onClickOutside(cellTypeMenu, () => {
  hideContextMenu();
});

function optionClicked(cellOption: CellTypeMenuOption) {
  hideContextMenu();
  if (!item.value) return;
  emit('option-clicked', {
    item: item.value,
    option: cellOption,
  });
}

function onEscKeyRelease(event: KeyboardEvent) {
  if (event.key === 'Escape' || event.code === 'Escape') {
    hideContextMenu();
  }
}

onMounted(() => {
  document.body.addEventListener('keyup', onEscKeyRelease);
});

onBeforeUnmount(() => {
  document.body.removeEventListener('keyup', onEscKeyRelease);
});
</script>

$styleBlockStart
<style lang="scss">
$light-grey: #ecf0f1;
$grey: #c8d0d4; // approximate darker variation of $light-grey
$blue: #007aff;
$white: #fff;
$black: #333;
.vue-simple-context-menu {
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  display: none;
  position: absolute;
  z-index: 1000000;
  background-color: $light-grey;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif;
  box-shadow: 0 3px 6px 0 rgba($black, 0.2);
  border-radius: 4px;
  min-width: 360px;
  &--active { display: flex; }
  &--two-col { flex-direction: row; }
  &__col { display: flex; flex-direction: column; min-height: 650px; overflow: auto; min-width: 280px; }
  &__col--left { min-width: 220px; }
  &__separator { width: 1px; background: #d9d9d9; }
  &__list { list-style: none; padding: 4px 0; margin: 0; }
  &__item { display: flex; color: $black; cursor: pointer; padding: 6px 14px; align-items: center; }
  &__item:hover { background-color: $blue; color: $white; }
  &__active { background-color: rgba(0, 122, 255, 0.4); }
  &__divider { box-sizing: content-box; height: 2px; padding: 5px 5px; pointer-events: none; color: darkgray; }
  &__label { flex: 1 1 auto; }
  &__group { margin: 0; padding: 4px 0; border-left: 3px solid transparent; }
  &__group.is-active { border-left-color: $blue; background: rgba(0, 122, 255, 0.06); }
  &__category { font-weight: 600; cursor: default; text-transform: uppercase; }
  
}
</style>
